import React, { useEffect, useState } from 'react';
import { Theme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  APIActions,
  Button,
  CustomDialog,
  Grid,
  HtmlTrans,
  Icon,
  Link,
  Progress,
  Typography,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { formatBytes } from 'web-library/utils/FileUtils';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { uploadToPresignLink } from '../../pages/accessArrangement/SelectCondition/components/UploadDocuments/components/useUploadFile';
import { useGetApplicationFileStatus } from '../../api-refactored/document/useGetApplicationFileStatus';
import { FileUploadState } from '../../models/File';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  fileBadgeParentContainer: {
    height: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  fileBadgeContainer: {
    height: theme.spacing(4),
    flexWrap: 'nowrap'
  },
  removeCell: {
    width: theme.spacing(12),
  },
  fileSize: {
    width: theme.spacing(10),
    justifySelf: 'start',
    whiteSpace: 'nowrap',
    color: theme.color.text.textTertiary,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    justifySelf: 'start',
    marginRight: theme.spacing(2),
  },
  documentIcon: {
    color: theme.color.text.textTertiary,
  },
  errorDocumentIcon: {
    color: theme.color.state.stateError,
  },
  fileNameContainer: {
    flexGrow: 2,
  },
  filename: {
    color: theme.color.text.textSecondary,
    flexGrow: 2,
  },
  filenameResolved: {
    textOverflow: 'ellipsis',
    fontWeight: 'bold',
    color: theme.color.link.linkDefault01,
    flexGrow: 2,
  },
  removeText: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: theme.color.state.stateError,
    fontWeight: 'bold',
  },
  removeButton: {
    padding: 0,
    height: theme.spacing(4),
  },
  removeIcon: {
    padding: 0,
    marginRight: theme.spacing(1),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(1),
  },
}));

const getIcon = (isLoading: boolean, hasError: boolean, classes: any) => {
  switch (true) {
    case isLoading:
      return <Progress size="M" type="circle" />;
    case hasError:
      return <Icon size="M" name={IconName.alertOff} className={classes.errorDocumentIcon} />;
    default:
      return <Icon size="M" name={IconName.document} className={classes.documentIcon} />;
  }
};

const fileKeyValue = (presignKey?: string) => presignKey || '';

interface IFileBadgeBaseProps {
  isLoading: boolean;
  presignKey?: string;
  onUpdateFileUploadComplete: (
    files: UploadedFile[],
    setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>,
    fileKey: string,
    uploadError: boolean
  ) => void;
}

interface FileBadgeProps extends IFileBadgeBaseProps {
  onUploadFileDelete: (file: File) => void;
  file: File;
  disableRemoveButton: boolean;
  hasError: boolean;
  presignedUrl?: string;
  uploadedFilesHandler: {
    files: UploadedFile[];
    setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  };
  downloadableLink?: boolean;
  buttonRemoveIdx?: number;
  hideRemoveButton?: boolean; // Already uploaded and verified
  isNewFile: boolean;
}

const ApplicationFileBadge = ({
  onUploadFileDelete,
  file,
  presignKey,
  disableRemoveButton,
  presignedUrl,
  isLoading,
  hasError,
  uploadedFilesHandler,
  onUpdateFileUploadComplete,
  downloadableLink = false,
  buttonRemoveIdx,
  hideRemoveButton = false,
  isNewFile,
}: FileBadgeProps) => {
  const [fileStatusSuccess, setFileStatusSuccess] = useState<boolean>(false);
  const [deleteModel, setDeleteModel] = useState<boolean>(false);
  const [enabledApiCall, setEnabledApiCall] = useState<boolean>(false);
  const [refetchInterval] = useState<number>(isNewFile ? 5000 : 1500);
  const { files, setFiles } = uploadedFilesHandler;
  const { t } = useTranslation();
  const { classes } = useStyles();

  useEffect(() => {
    if (presignedUrl && presignKey) {
      uploadToPresignLink(presignedUrl, presignKey, file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presignKey, presignedUrl]);

  const { isLoading: applicationFileIsLoading } = useGetApplicationFileStatus({
    fileKey: fileKeyValue(presignKey),
    enabled:
      !!presignKey &&
      ((downloadableLink && enabledApiCall) ||
        (isNewFile && !hideRemoveButton && !fileStatusSuccess)),
    refetchInterval: !fileStatusSuccess ? refetchInterval : undefined,
    onSuccess: (data) => {
      if (data.fileStatus === FileUploadState.SUCCESS && presignKey) {
        setFileStatusSuccess(true);
        onUpdateFileUploadComplete(files, setFiles, presignKey, false);

        if (enabledApiCall) {
          setEnabledApiCall(false);
          const newWindow = window.open(data.url, '_blank', 'noopener,noreferrer');
          if (newWindow) {
            newWindow.opener = null;
          }
        }
      }
      if (data.fileStatus === FileUploadState.FAILED && presignKey) {
        setFileStatusSuccess(true);
        onUpdateFileUploadComplete(files, setFiles, presignKey, true);
      }
    },
  });

  const displayIcon = () => getIcon(isLoading || applicationFileIsLoading, hasError, classes);

  const fileNameText = () => (
    <Typography
      noWrap
      className={`${!!presignKey && !isLoading && !applicationFileIsLoading && !hasError
        ? classes.filenameResolved
        : classes.filename
        }`}
    >
      {file.name}
    </Typography>
  );

  const clickDownloadFile = () => {
    setEnabledApiCall(true);
  };

  return (
    (<Grid className={classes.fileBadgeParentContainer} >
      <Grid
        container
        md={8}
        xs={12}
        direction="row"
        alignItems="center"
        justify="flex-start"
        wrap="nowrap"
        className={classes.fileBadgeContainer}
      >
        <Grid className={classes.iconContainer}>{displayIcon()}</Grid>
        {downloadableLink && !!presignKey ? (
          <Button
            // target="_blank"
            variant='text'
            style={{ overflow: 'hidden', flexGrow: 2 }}
            onClick={clickDownloadFile}
            rel="noopener noreferrer"
          >
            {fileNameText()}
          </Button>
        ) : (
          fileNameText()
        )}

        {!isLoading && !hasError && (
          <>
            <Grid container justify="flex-end" className={classes.fileSize}>
              {formatBytes(file.size)}
            </Grid>
            <Grid container className={classes.removeCell}>
              {!hideRemoveButton && (
                <Button
                  id={`button-remove-${buttonRemoveIdx}`}
                  className={`${classes.removeButton} removeButton`}
                  variant="text"
                  onClick={() => setDeleteModel(true)}
                  disabled={disableRemoveButton}
                >
                  <Typography variant="body2" className={classes.removeText}>
                    <Icon
                      size="S"
                      color="error"
                      name={IconName.trash}
                      className={classes.removeIcon}
                    />
                    {t('accessArrangement->>SelectCondition.FileBadgeRemove')}
                  </Typography>
                </Button>
              )}
            </Grid>
          </>
        )}
      </Grid>
      {hasError && (
        <Typography className={classes.errorText}>
          {t('accessArrangement->>SelectCondition.UploadFailed')}
        </Typography>
      )}
      <CustomDialog
        dialogSize="md"
        open={deleteModel}
        title={t('components->>RemoveFile.FileRemoveTitle')}
        message={
          <HtmlTrans
            i18nKey="components->>RemoveFile.FileRemoveConfirmationMsg"
            values={{
              fileName: file.name,
            }}
          />
        }
        action={
          <APIActions
            onClose={() => setDeleteModel(false)}
            apiCall={() => onUploadFileDelete(file)}
            cancelString={t('components->>RemoveFile.Cancel')}
            apiString={t('components->>RemoveFile.Proceed')}
          />
        }
      />
    </Grid>)
  );
};

export default ApplicationFileBadge;
